import React, { useState, useRef, useEffect } from 'react';
import { useSpecialistAuth } from '../../context/SpecialistAuthContext';
import SpecialistDashboardLayout from '../../components/SpecialistDashboardLayout';
import { API_URL, API_BASE_URL } from '../../config/api';

const SpecialistProfilePage = () => {
  const { specialist, updateProfile, uploadAvatar } = useSpecialistAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [salesCategoriesList, setSalesCategoriesList] = useState([]);
  const [projectCategoriesList, setProjectCategoriesList] = useState([]);
  const [formData, setFormData] = useState({
    name: specialist?.name || '',
    email: specialist?.email || '',
    phone: specialist?.phone || '',
    title: specialist?.title || '',
    category: specialist?.category || '',
    salesCategory: specialist?.salesCategory || '',
    projectCategory: specialist?.projectCategory || '',
    expertise: specialist?.expertise || '',
    experience: specialist?.experience || '',
    country: specialist?.country || '',
    city: specialist?.city || '',
    interestedLocations: specialist?.interestedLocations || '',
    companyName: specialist?.companyName || '',
    bio: specialist?.bio || '',
  });

  useEffect(() => {
    const fetchSpecCategories = async () => {
      try {
        const [salesRes, projectRes] = await Promise.all([
          fetch(`${API_BASE_URL}/settings/sales-categories`),
          fetch(`${API_BASE_URL}/settings/project-categories`)
        ]);
        if (salesRes.ok) setSalesCategoriesList(await salesRes.json());
        if (projectRes.ok) setProjectCategoriesList(await projectRes.json());
      } catch (err) {
        console.error('Error loading specialist categories:', err);
      }
    };
    fetchSpecCategories();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      // Upload avatar first if changed
      if (avatarFile) {
        await uploadAvatar(avatarFile);
      }
      await updateProfile(formData);
      setAvatarFile(null);
      setAvatarPreview(null);
      setIsEditing(false);
      alert('Profile updated successfully!');
    } catch (error) {
      alert('Failed to update profile');
    } finally {
      setUploading(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const defaultImage = '/uploads/builder-images/f2c82df6909e2e7512c02281532c47940c81ce19_w520.png';
  const getAvatarSrc = () => {
    if (avatarPreview) return avatarPreview;
    if (specialist?.avatar) {
      if (specialist.avatar.startsWith('http')) return specialist.avatar;
      const baseUrl = API_URL.replace('/api', '');
      return `${baseUrl}${specialist.avatar}`;
    }
    return defaultImage;
  };

  const handleEditClick = () => {
    setFormData({
      name: specialist?.name || '',
      email: specialist?.email || '',
      phone: specialist?.phone || '',
      title: specialist?.title || '',
      category: specialist?.category || '',
      salesCategory: specialist?.salesCategory || '',
      projectCategory: specialist?.projectCategory || '',
      expertise: specialist?.expertise || '',
      experience: specialist?.experience || '',
      country: specialist?.country || '',
      city: specialist?.city || '',
      interestedLocations: specialist?.interestedLocations || '',
      companyName: specialist?.companyName || '',
      bio: specialist?.bio || '',
    });
    setIsEditing(true);
  };

  // Determine verification text
  const isVerified = specialist?.isEmailVerified;
  const membershipPlan = specialist?.membershipPlan || 'basic';
  const membershipLabel = membershipPlan.charAt(0).toUpperCase() + membershipPlan.slice(1);

  const location = [specialist?.city, specialist?.country].filter(Boolean).join(', ') || '-';

  const fieldStyle = {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '6px',
  };

  // ===== EDIT MODE =====
  if (isEditing) {
    return (
      <SpecialistDashboardLayout>
        <div style={{ padding: '0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#1F396C', margin: 0 }}>Edit Specialist Profile</h1>
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 24px', background: '#fff', color: '#333',
                border: '1px solid #ddd', borderRadius: '8px',
                fontSize: '14px', fontWeight: '500', cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>

          {/* Profile Image Upload */}
          <div style={{ 
            display: 'flex', alignItems: 'center', gap: '20px', 
            marginBottom: '28px', padding: '20px', 
            background: '#fff', borderRadius: '12px', border: '1px solid #eee' 
          }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <img
                src={getAvatarSrc()}
                alt="Profile"
                style={{ 
                  width: '80px', height: '80px', borderRadius: '50%', 
                  objectFit: 'cover', border: '3px solid #eee' 
                }}
                onError={(e) => { e.target.src = defaultImage; }}
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  position: 'absolute', bottom: '-2px', right: '-2px',
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: '#1F396C', border: '2px solid #fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#2a4f8f'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#1F396C'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 17C14.2091 17 16 15.2091 16 13C16 10.7909 14.2091 9 12 9C9.79086 9 8 10.7909 8 13C8 15.2091 9.79086 17 12 17Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />
            </div>
            <div>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#333', margin: '0 0 4px 0' }}>Profile Photo</p>
              <p style={{ fontSize: '12px', color: '#888', margin: '0 0 8px 0' }}>JPG, PNG or GIF. Max 5MB.</p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  padding: '6px 16px', background: '#f5f5f5', color: '#333',
                  border: '1px solid #ddd', borderRadius: '6px',
                  fontSize: '13px', cursor: 'pointer', fontWeight: '500'
                }}
              >
                {avatarPreview ? 'Change Photo' : 'Upload Photo'}
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} style={{ maxWidth: '100%' }}>
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #eee', padding: '32px', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1F396C', margin: '0 0 24px 0' }}>Personal Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={labelStyle}>Full Name</label>
                  <input name="name" value={formData.name} onChange={handleChange} style={fieldStyle} placeholder="Enter your name" />
                </div>
                <div>
                  <label style={labelStyle}>Title</label>
                  <input name="title" value={formData.title} onChange={handleChange} style={fieldStyle} placeholder="e.g., Architecture Engineer" />
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input name="email" value={formData.email} onChange={handleChange} style={fieldStyle} placeholder="Enter your email" type="email" />
                </div>
                <div>
                  <label style={labelStyle}>Phone</label>
                  <input name="phone" value={formData.phone} onChange={handleChange} style={fieldStyle} placeholder="Enter phone number" />
                </div>
                <div>
                  <label style={labelStyle}>Country</label>
                  <input name="country" value={formData.country} onChange={handleChange} style={fieldStyle} placeholder="Country" />
                </div>
                <div>
                  <label style={labelStyle}>City</label>
                  <input name="city" value={formData.city} onChange={handleChange} style={fieldStyle} placeholder="City" />
                </div>
              </div>
            </div>

            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #eee', padding: '32px', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1F396C', margin: '0 0 24px 0' }}>Professional Details</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={labelStyle}>Sales Category</label>
                  <select name="salesCategory" value={formData.salesCategory} onChange={handleChange} style={fieldStyle}>
                    <option value="">Select Sales Category</option>
                    {salesCategoriesList.map(sc => (
                      <option key={sc.id} value={sc.name}>{sc.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Project Category</label>
                  <select name="projectCategory" value={formData.projectCategory} onChange={handleChange} style={fieldStyle}>
                    <option value="">Select Project Category</option>
                    {projectCategoriesList.map(pc => (
                      <option key={pc.id} value={pc.name}>{pc.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Expertise</label>
                  <input name="expertise" value={formData.expertise} onChange={handleChange} style={fieldStyle} placeholder="e.g., Office Architecture, Home Architect" />
                </div>
                <div>
                  <label style={labelStyle}>Experience</label>
                  <input name="experience" value={formData.experience} onChange={handleChange} style={fieldStyle} placeholder="e.g., 12 years" />
                </div>
                <div>
                  <label style={labelStyle}>Interested Locations</label>
                  <input name="interestedLocations" value={formData.interestedLocations} onChange={handleChange} style={fieldStyle} placeholder="e.g., USA, Canada, Dubai" />
                </div>
                <div>
                  <label style={labelStyle}>Company Name</label>
                  <input name="companyName" value={formData.companyName} onChange={handleChange} style={fieldStyle} placeholder="Your company name" />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Bio</label>
                <textarea name="bio" value={formData.bio} onChange={handleChange} style={{ ...fieldStyle, minHeight: '100px', resize: 'vertical' }} placeholder="Tell us about your expertise..." />
              </div>
            </div>

            <button type="submit" disabled={uploading} style={{
              padding: '12px 32px', background: uploading ? '#8899b5' : '#1F396C', color: '#fff',
              border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: uploading ? 'not-allowed' : 'pointer'
            }}>
              {uploading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      </SpecialistDashboardLayout>
    );
  }

  // ===== PREVIEW MODE (Default) =====
  return (
    <SpecialistDashboardLayout>
      <div style={{ padding: '0' }}>
        {/* Header Row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#222', margin: 0 }}>Specialists Account</h1>
          <button
            type="button"
            onClick={handleEditClick}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 24px', background: '#fff', color: '#333',
              border: '1px solid #ddd', borderRadius: '8px',
              fontSize: '14px', fontWeight: '500', cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
            }}
            onMouseEnter={(e) => { e.target.style.borderColor = '#1F396C'; e.target.style.color = '#1F396C'; }}
            onMouseLeave={(e) => { e.target.style.borderColor = '#ddd'; e.target.style.color = '#333'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ pointerEvents: 'none' }}>
              <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Edit Account
          </button>
        </div>

        {/* Verification Levels */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#333', margin: '0 0 8px 0' }}>Verification Levels</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {isVerified ? (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10M21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21C10.8181 21 9.64778 20.7672 8.55585 20.3149C7.46392 19.8626 6.47177 19.1997 5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12Z" stroke="#2196F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#2196F3' }}>Verified</span>
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="9" stroke="#999" strokeWidth="2"/>
                </svg>
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#999' }}>Not Verified</span>
              </>
            )}
          </div>
          <span
            style={{ fontSize: '12px', color: '#E74C3C', cursor: 'pointer', marginTop: '4px', display: 'inline-block', fontWeight: '500' }}
            onClick={() => window.location.href = '/specialist-membership'}
          >
            upgrade now
          </span>
        </div>

        {/* Membership Plan */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#333', margin: '0 0 8px 0' }}>Membership Plan</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="9" stroke="#aaa" strokeWidth="2" fill={membershipPlan === 'basic' ? '#ddd' : membershipPlan === 'professional' ? '#FFD700' : '#1F396C'}/>
            </svg>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>{membershipLabel}</span>
          </div>
          <span
            style={{ fontSize: '12px', color: '#E74C3C', cursor: 'pointer', marginTop: '4px', display: 'inline-block', fontWeight: '500' }}
            onClick={() => window.location.href = '/specialist-membership'}
          >
            upgrade now
          </span>
        </div>

        {/* Divider */}
        <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '0 0 28px 0' }} />

        {/* Profile Details */}
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1F396C', margin: '0 0 4px 0' }}>
            {specialist?.title || specialist?.category || 'Specialist'}
          </h2>
          <p style={{ fontSize: '15px', color: '#444', margin: '0 0 20px 0' }}>
            {specialist?.name || '-'}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#333', minWidth: '160px' }}>Location:</span>
              <span style={{ fontSize: '14px', color: '#666' }}>{location}</span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#333', minWidth: '160px' }}>Category:</span>
              <span style={{ fontSize: '14px', color: '#666' }}>{specialist?.category || '-'}</span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#333', minWidth: '160px' }}>Expertise:</span>
              <span style={{ fontSize: '14px', color: '#666' }}>{specialist?.expertise || '-'}</span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#333', minWidth: '160px' }}>Interested location:</span>
              <span style={{ fontSize: '14px', color: '#666' }}>{specialist?.interestedLocations || '-'}</span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#333', minWidth: '160px' }}>Experience:</span>
              <span style={{ fontSize: '14px', color: '#666' }}>{specialist?.experience || '-'}</span>
            </div>
          </div>
        </div>
      </div>
    </SpecialistDashboardLayout>
  );
};

export default SpecialistProfilePage;
